import { NgModule, ErrorHandler, Injectable, CUSTOM_ELEMENTS_SCHEMA, APP_INITIALIZER } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserModule, Title } from '@angular/platform-browser';
import {NavbarModule} from 'src/app/components/navbar/navbar.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HttpErrorResponse } from '@angular/common/http';
import { WindowRefService } from './services/window-ref.service';
import {CommonModule} from '@angular/common';
import { MatBottomSheetModule, MatCardModule, MatButtonModule, MatInputModule, MatFormFieldModule, MatIconModule } from '@angular/material';
import {  ReactiveFormsModule} from '@angular/forms';
import { MobileComponent } from 'src/app/pages/checkout-process/mobile/mobile.component';
import { MobileModule } from './components/mobile/mobile.module';
import { Platform } from '@angular/cdk/platform';
import * as Sentry from "@sentry/browser";
import { environment } from "../environments/environment.test";


Sentry.init({
  dsn: "https://49fa244ff53145b69963037aca9f2e7b@o435098.ingest.sentry.io/5392963",
  // TryCatch has to be configured to disable XMLHttpRequest wrapping, as we are going to handle
  // http module exceptions manually in Angular's ErrorHandler and we don't want it to capture the same error twice.
  // Please note that TryCatch configuration requires at least @sentry/browser v5.16.0.
  integrations: [new Sentry.Integrations.TryCatch({
    XMLHttpRequest: false,
  })],
});

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  constructor() {}

  extractError(error) {
    // Try to unwrap zone.js error.
    // https://github.com/angular/angular/blob/master/packages/core/src/util/errors.ts
    if (error && error.ngOriginalError) {
      error = error.ngOriginalError;
    }

    // We can handle messages and Error objects directly.
    if (typeof error === "string" || error instanceof Error) {
      return error;
    }

    // If it's http module error, extract as much information from it as we can.
    if (error instanceof HttpErrorResponse) {
      // The `error` property of http exception can be either an `Error` object, which we can use directly...
      if (error.error instanceof Error) {
        return error.error;
      }

      // ... or an`ErrorEvent`, which can provide us with the message but no stack...
      if (error.error instanceof ErrorEvent) {
        return error.error.message;
      }

      // ...or the request body itself, which we can use as a message instead.
      if (typeof error.error === "string") {
        return `Server returned code ${error.status} with body "${error.error}"`;
      }

      // If we don't have any detailed information, fallback to the request message itself.
      return error.message;
    }

    // Skip if there's no error, and let user decide what to do with it.
    return null;
  }

  handleError(error) {
    const extractedError = this.extractError(error) || "Handled unknown error";

    // Capture handled exception and send it to Sentry.
    const eventId = Sentry.captureException(extractedError);

    // When in development mode, log the error to console for immediate feedback.
    if (!environment.production) {
      console.error(extractedError);
    }

    // Optionally show user dialog to provide details on what happened.
    Sentry.showReportDialog({ eventId });
  }
}


import { BillingAddressComponent } from './components/billing-address/billing-address.component';
import { LowerCaseUrlSerializer } from './case-insensitive-url';
import { UrlSerializer } from '@angular/router';
import { ExternalRouterService } from './services/external-router.service';
import { UtilsModule } from './utils/utils.module';
import { LoginNameService } from './services/login-name.service';
import { DropDownModule } from './drop-down/drop-down.module';
import { OnelapCheckoutLoginEventService } from './pages/checkout-process/onelap-chekout-login/onelap-checkout-login-event.service';
import { ErrorNotificationService } from './shipper/error-notification.service';
import { ProductDetailDescriptionComponent } from './product-detail-description/product-detail-description.component';
import { CommonFunctionalityComponent } from './common-functionality/common-functionality.component';
import { DotElasticModule } from './dot-elastic/dot-elastic.module';
import { UnderscorePipe } from './underscore/underscore.pipe';
import { AppConfigService } from './app-config.service';
import { CouponPipe } from './coupon-pipe/coupon.pipe';
import { VariantPipe } from './utils/variant.pipe';
import { WebOtpDirective } from './web-otp.directive';
import { DashcamAccessoriesComponent } from './components/dashcam-accessories/dashcam-accessories.component';
import { RearCameraBottomSheetComponent } from './components/rear-camera-bottom-sheet/rear-camera-bottom-sheet.component';
import { GpsLoggerBottomSheetComponent } from './components/gps-logger-bottom-sheet/gps-logger-bottom-sheet.component';
import { HardwiringKitBottomSheetComponent } from './components/hardwiring-kit-bottom-sheet/hardwiring-kit-bottom-sheet.component';
import { CarChargerBottomSheetComponent } from './components/car-charger-bottom-sheet/car-charger-bottom-sheet.component';
import { ProductsFaqsComponent } from './components/products-faqs/products-faqs.component';
import { AllCouponSetComponent } from './components/all-coupon-set/all-coupon-set.component';
import { ClaimRewardSheetComponent } from './components/claim-reward-sheet/claim-reward-sheet.component';
import { SuccessfulComponent } from './components/successful/successful.component';
import { GpsProtectionComponent } from './components/gps-protection/gps-protection.component';
import { TalkToExpertMobileComponent } from './talk-to-expert-mobile/talk-to-expert-mobile.component';
import { AmazonVsOnelapComponent } from './components/amazon-vs-onelap/amazon-vs-onelap.component';
import { LazyLoadObserverDirective } from './directives/lazy-load-observer.directive';
import { AiChatComponent } from './components/ai-chat/ai-chat.component';



const magicCheckoutEnvInitializerFn = (appConfig: AppConfigService) => {
  return () => {
      return appConfig.loadAppConfig();
  }
};
const bigDealInitializerFn = (appConfig: AppConfigService) => {
  return () => {
      return appConfig.loadBigDeal();
  }
};



@NgModule({
  declarations: [
    AppComponent,
    BillingAddressComponent,
    CommonFunctionalityComponent,
    DashcamAccessoriesComponent,
    RearCameraBottomSheetComponent,
    GpsLoggerBottomSheetComponent,
    HardwiringKitBottomSheetComponent,
    CarChargerBottomSheetComponent,
    ProductsFaqsComponent,
    AllCouponSetComponent,
    ClaimRewardSheetComponent,
    SuccessfulComponent,
    GpsProtectionComponent,
    AiChatComponent,

    
   
   
     ], 
  imports: [
    ReactiveFormsModule,
    HttpClientModule,
    AppRoutingModule,
    NavbarModule,
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    MatBottomSheetModule,
    MatCardModule,
    MatButtonModule,
    MobileModule,
    UtilsModule,
    DropDownModule,
    DotElasticModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    
    
  ],
  
  providers: [WindowRefService,Platform,Title, {
    provide: UrlSerializer,
    useClass: LowerCaseUrlSerializer
  },
  AppConfigService,
    {
        provide: APP_INITIALIZER,
        useFactory: magicCheckoutEnvInitializerFn,
        multi: true,
        deps: [AppConfigService]
    },
    {
      provide: APP_INITIALIZER,
      useFactory: bigDealInitializerFn,
      multi:true,
      deps: [AppConfigService]
    },
  OnelapCheckoutLoginEventService,
  LoginNameService,
  ErrorNotificationService,
  ExternalRouterService],
  bootstrap: [AppComponent],

  entryComponents: [MobileComponent,
    DashcamAccessoriesComponent,
    RearCameraBottomSheetComponent,
    GpsLoggerBottomSheetComponent,
    HardwiringKitBottomSheetComponent,
    CarChargerBottomSheetComponent,
  ProductsFaqsComponent,
  AllCouponSetComponent,
  ClaimRewardSheetComponent,
  SuccessfulComponent,
  GpsProtectionComponent
],
})
export class AppModule {  }